import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  TextField,
  IconButton,
  Button,
  makeStyles,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";

import StyledButton from "../../../../common/models/Button";
import SaveIcon from "@material-ui/icons/Save";
import CancelIcon from "@material-ui/icons/Cancel";
import { appUrl } from "../../../../../utils/config";
import axios from "axios";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: theme.spacing(2),
  },
  table: {
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    width: "1000px",
  },
  headerCell: {
    fontWeight: 600,
    color: "#333",
    backgroundColor: "#f0f0f0",
    padding: "10px 16px",
    textAlign: "left",
  },
  bodyCell: {
    padding: "10px 16px",
    textAlign: "left",
    verticalAlign: "middle",
  },
  textField: {
    "& input": {
      padding: "6px 8px",
      height: "30px",
    },
  },
  addButton: {
    marginTop: theme.spacing(2),
    backgroundColor: "#fff",
    color: "#000",
    borderRadius: "20px",
    padding: "6px 16px",
    textTransform: "none",
    fontWeight: 600,
    border: "1px solid #000",
    display: "flex",
    alignItems: "center",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  addIconCircle: {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    border: "2px solid #000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(1),
    fontSize: "16px",
    fontWeight: 600,
  },
  iconBtn: {
    padding: "2px",
  },
  inlineActions: {
    display: "flex",
    flexDirection: "column",
    marginLeft: 8,
  },
  deleteIcon: {
    color: theme.palette.error.main,
  },
  switchBase: {
    "&.Mui-checked": {
      color: "#007bff",
    },
    "&.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#007bff",
    },
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    marginTop: theme.spacing(4),
  },
  backButton: {
    backgroundColor: "#555",
    color: "#fff",
    borderRadius: "20px",
    padding: "8px 24px",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#333",
    },
  },
  resetChangesButton: {
    border: "1px solid #000000",
    color: "#000000",
    fontWeight: 600,
    borderRadius: "20px",
    padding: "6px 16px",
    textTransform: "none",
    marginTop: theme.spacing(2),
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  saveButton: {
    backgroundColor: "#f26522",
    color: "#fff",
    borderRadius: "20px",
    padding: "8px 24px",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#d4551a",
    },
  },
}));

const PlantTypeTab = (props) => {
  const { featuresAccess, sponsorId } = props;
  const classes = useStyles();

  const [plantTypes, setPlantTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const [isUpdated, setIsUpdated] = useState(false);
  const [savingStates, setSavingStates] = useState({});
  
  // Get edit access from featuresAccess
  const canEdit = featuresAccess?.editSponsorNetworkDetails ?? false;

  const dispatch = (action) => {
    if (action.type === "OPEN_BACKDROP") {
      //console.log("Opening backdrop");
    } else if (action.type === "DESTROY_BACKDROP") {
      //console.log("Closing backdrop");
    }
  };

  const fetchPlantTypes = async () => {
    setError(null);
    setLoading(true);
    dispatch({ type: "OPEN_BACKDROP" });

    try {
      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchPlantTypesDirect/false`;
      const response = await axios.get(url);
      const data = response?.data?.data;

      //console.log("API Plant Type Data:", data);

      if (data?.status === true || data?.status === "true") {
        const list = data.plantTypeList || [];

        const formatted = list.map((item, index) => ({
          ...item,
          id: item.plantTypeID,
          name: item.plantTypeName,
          selected: !!item.activated,
          isNew: false,
          supplyType: item.plantType,
        }));

        setPlantTypes(formatted);
        //console.log(data, "PlantTypeTab", "plantTypes");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error("API Error:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
      dispatch({ type: "DESTROY_BACKDROP" });
    }
  };

  useEffect(() => {
    fetchPlantTypes();
  }, []);

  const saveNewPlantType = async () => {
    try {
      const newPlantTypes = plantTypes.filter((plantType) => plantType.isNew);
      if (newPlantTypes.length === 0) {
        //console.log("No new plant types to save.");
        return;
      }

      const payload = {
        sponsorId: sponsorId,
        ecosystemId: localStorage.getItem("ecosystemId"),
        plantTypeList: newPlantTypes.map(({ id, name, selected }) => ({
          plantType: "New",
          plantTypeName: name,
          activated: selected,
        })),
      };

      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/addPlantTypes`;
      const response = await axios.post(url, payload);
      fetchPlantTypes();
      props.setPlantType((prev) => !prev); 
      if (response.data?.data?.status === true) {
        //console.log("New plant types saved successfully.");
      } else {
        console.error("Failed to save new plant types.");
      }
    } catch (error) {
      console.error("Error saving new plant types:", error);
    }
  };

  const deletePlantType = async (plantType) => {
    //console.log(plantType, "Deleting plantType");
    try {
      if (plantType.isNew) {
        setPlantTypes((prevPlantTypes) =>
          prevPlantTypes.filter((item) => item.id !== plantType.id)
        );
      } else {
        const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updatePlantTypes`;
        const payload = {
          sponsorId: sponsorId,
          ecosystemId: localStorage.getItem("ecosystemId"),
          plantTypeList: [
            {
              plantTypeID: plantType.id,
              plantTypeName: plantType.name,
              activated: plantType.selected,
              markForDelete: true,
            },
          ],
        };
        //console.log(plantType, "Debugging plantType at index:");
        const response = await axios.post(url, payload);
        fetchPlantTypes();
        props.setPlantType((prev) => !prev); 
        if (response.data?.data?.status === true) {
          //console.log("Plant type deleted successfully.");
        } else {
          console.error("Failed to delete plant type.");
        }
      }
    } catch (error) {
      console.error("Error deleting plant type:", error);
    }
  };

  const handleSaveChanges = async () => {
    try {
      // Check if at least one plant type is selected
      const isAnyPlantTypeSelected = plantTypes.filter(
        (plantType) => plantType.isUpdated
      );

      if (!isAnyPlantTypeSelected.length) {
        //console.log("No updates detected. Proceeding to the next step.");

        return;
      }
      // if (!isAnyPlantTypeSelected.length) {
      //   setErrorMessage("Please select at least one plant type.");
      //   return;
      // }
      if (plantTypes.every((plantType) => !plantType.selected)) {
        setErrorMessage("Please enable at least one in toggle mode.");
        return;
      }

      // Prepare the payload
      const payload = {
        sponsorId: sponsorId,
        ecosystemId: localStorage.getItem("ecosystemId"),
        plantTypeList: isAnyPlantTypeSelected.map(({ id, name, selected }) => ({
          plantTypeID: id,
          plantTypeName: name,
          activated: selected,
          markForDelete: false,
        })),
      };

      // Call the API
      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updatePlantTypes`;
      const response = await axios.post(url, payload);

      if (response.data?.data?.status === true) {
        //console.log("Plant types updated successfully.");
        fetchPlantTypes(); // Refresh the list after successful update
        // Proceed to the next step
      } else {
        console.error("Failed to update plant types.");
        setErrorMessage("Failed to update plant types. Please try again.");
      }
    } catch (error) {
      console.error("Error updating plant types:", error);
      setErrorMessage(
        "An error occurred while updating plant types. Please try again."
      );
    }
  };

  const handleToggle = (index) => {
    const updated = [...plantTypes];
    updated[index].selected = !updated[index].selected;
    updated[index].isUpdated = true;
    setPlantTypes(updated);
    setIsUpdated(true);
  };

  const handleNameChange = (index, value) => {
    const updated = [...plantTypes];
    updated[index].name = value;
    setPlantTypes(updated);
    setIsUpdated(true);
  };

  // const handleDelete = (index) => {
  //   const updated = plantTypes.filter((_, i) => i !== index);
  //   setPlantTypes(updated);
  // };

  const handleAddPlantType = () => {
    // const nextId = (plantTypes.length + 1).toString().padStart(2, "0");
    setPlantTypes([
      ...plantTypes,
      {
        // id: nextId,
        name: "",
        selected: false,
        isNew: true,
      },
    ]);
  };

  const handleSaveNew = async (index) => {
    const plantType = plantTypes[index];
    if (!plantType.name?.trim() || savingStates[index]) return;
    setSavingStates((prev) => ({ ...prev, [index]: true }));
    const success = await saveNewPlantType(plantType);
    if (success) {
      // Refresh full list from API after successful save
      await fetchPlantTypes();
    } else {
      // alert("Failed to save new plantType.");
    }
    setSavingStates((prev) => ({ ...prev, [index]: false }));
  };

  const handleCancel = (index) => {
    const plantType = plantTypes[index]; // Get the plantType by index
    // Log plantType and index
    const updated = plantTypes.filter((_, i) => i !== index);
    setPlantTypes(updated);
    //console.log(updated, "PlantTypeTab", "plantTypes");
  };
  const handleResetChanges = () => {
    if (isUpdated) {
      fetchPlantTypes();
      setIsUpdated(false);
      setErrorMessage("");
    }
  };

  return (
    <div
      className={classes.container}
      style={{ display: "flex", flexDirection: "column", gap: "24px" }}
    >
      {/* Table and Buttons Section */}
      <div style={{ display: "flex", gap: "40px", alignItems: "flex-start" }}>
        <Table className={`${classes.table} default-table-styles`}>
          <TableHead>
            <TableRow>
              <TableCell className={`${classes.headerCell} default-table-styles`}>Selection</TableCell>
              <TableCell className={`${classes.headerCell} default-table-styles`}>Plant Type</TableCell>
              <TableCell className={`${classes.headerCell} default-table-styles`} style={{whiteSpace: 'nowrap'}}>
                Plant Type ID
              </TableCell>
              <TableCell className={`${classes.headerCell} default-table-styles`}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100px",
                    }}
                  >
                    <CircularProgress />
                  </div>
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "60px",
                      backgroundColor: "#ffecec",
                      color: "#b00020",
                      border: "1px solid #ff9999",
                    }}
                  >
                    <Typography>{error}</Typography>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              plantTypes.map((plantType, index) => (
                <TableRow key={plantType.id}>
                  <TableCell className={classes.bodyCell}>
                    <Switch
                      checked={plantType.selected}
                      onChange={() => handleToggle(index)}
                      classes={{ switchBase: classes.switchBase }}
                      disabled={!canEdit}
                    />
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    {plantType.isNew ? (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <TextField
                          placeholder="Type Text Here"
                          value={plantType.name}
                          onChange={(e) =>
                            handleNameChange(index, e.target.value)
                          }
                          onBlur={(e) => {
                            const { value } = e.target;
                            // Trim leading and trailing spaces for string values
                            if (typeof value === "string" && value.trim() !== value) {
                              handleNameChange(index, value.trim());
                            }
                          }}
                          className={classes.textField}
                          variant="outlined"
                          inputProps={{
                            onBeforeInput: (e) => {
                              const allowedChars = /^[a-zA-Z\s\-\/,().]+$/;
                              if (!allowedChars.test(e.data)) {
                                e.preventDefault();
                              }
                            }
                          }}
                        />
                        <div className={classes.inlineActions}>
                          <IconButton
                            onClick={() => handleCancel(index)}
                            className={classes.iconBtn}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={() => handleSaveNew(index)}
                            className={classes.iconBtn}
                            disabled={
                              !plantTypes[index].name?.trim() ||
                              plantTypes.some(
                                (p, i) => i !== index &&
                                  p.name?.trim().toLowerCase() === plantTypes[index].name?.trim().toLowerCase()
                              ) || savingStates[index]
                            }
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </div>
                    ) : (
                      plantType.name
                    )}
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    {plantType.id}
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    {canEdit && (plantType.isNew == true ||
                      (plantType.supplyType === "New" &&
                        plantType.lastUpdatedBy !== "support@schrocken.com")) && (
                      <IconButton
                        aria-label="delete"
                        onClick={() => deletePlantType(plantType)}
                      >
                        <DeleteIcon className={classes.deleteIcon} />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <div
          className={classes.footer}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px", // Space between buttons
            marginTop: "16px", // Space between table and buttons
            marginLeft: "7rem",
          }}
        >
          <Button
            className={classes.saveButton}
            type="button"
            onClick={handleSaveChanges}
            disabled={!isUpdated || !canEdit}
            style={{
    
    opacity: !isUpdated || !canEdit ? 0.7 : 1, // Fade out when disabled
    color: "#fff", 
  }}
          >
            Save Selection
          </Button>
          <Button
            className={classes.resetChangesButton}
            type="button"
            onClick={handleResetChanges}
             disabled={!isUpdated || !canEdit} // or whatever your "no changes" condition is
  style={{
    opacity: !isUpdated || !canEdit ? 0.8 : 1, // Fade out when disabled
  }}
          >
            Reset Selection
          </Button>
        </div>
      </div>
      {errorMessage && (
        <div style={{ color: "red" }} className="error-message">
          {errorMessage}
        </div>
      )}
      {canEdit && (
        <Button
          variant="outlined"
          onClick={handleAddPlantType}
          className={classes.addButton}
        >
          <span className={classes.addIconCircle}>+</span>
          Add New Plant Type
        </Button>
      )}

      {/* <div className={classes.footer}>
        <Button
          className={classes.backButton}
          onClick={() => {
            props.handleBack();
          }}
        >
          &lt; Back
        </Button>
        <StyledButton
          className={classes.saveButton}
          onClick={() => {
            if (isUpdated) {
              handleNextClick(); // Call API and move to the next tab if updates are made
            } else {
              props.handleNext(); // Directly move to the next tab if no updates are made
            }
          }}
        >
          Save & Next
        </StyledButton>
      </div> */}
    </div>
  );
};

export default PlantTypeTab;
